import { useCallback, useEffect, useRef, useState } from 'react';
import { useWebSocket } from './WebSocketContext';
import { useSnackbar } from '../Utils/SnackBarContext';

/**
 * Generic storage upload helper for uploading files via websocket and receiving a response.
 * Keeps upload UX/notifications consistent and reusable.
 */
export function useStorageUpload({
  command = 'uploadTemplateFile',
  caseid,
  accept = '',
  successMessage = 'Upload completed',
  errorMessage = 'Upload failed',
  sendingMessage = 'Uploading...',
  onSuccess,
  onError
}) {
  const { sendMessage, subscribeToMessages, ws } = useWebSocket();
  const { openSnackbar } = useSnackbar();
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const pendingFileRef = useRef(null);
  const pendingTimerRef = useRef(null);
  const isSocketOpen = ws && ws.readyState === WebSocket.OPEN;
  const CONNECTION_WAIT_MS = 2000;

  useEffect(() => {
    const unsubscribe = subscribeToMessages([command], (message) => {
      const { status, message: serverMessage } = message.data || {};
      if (status === 'success') {
        openSnackbar(serverMessage || successMessage, 4000);
        if (onSuccess) onSuccess(message.data);
      } else {
        openSnackbar(serverMessage || errorMessage, 4000);
        if (onError) onError(message.data);
      }
      setUploading(false);
    });
    return unsubscribe;
  }, [command, subscribeToMessages, openSnackbar, successMessage, errorMessage, onSuccess, onError]);

  const triggerFileDialog = useCallback(() => {
    if (uploading) return;
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, [uploading]);

  const startUpload = useCallback((file) => {
    const reader = new FileReader();
    setUploading(true);
    reader.onload = () => {
      try {
        sendMessage({
          command,
          caseid,
          files: [{
            filename: file.name,
            filedata: reader.result
          }]
        });
        openSnackbar(sendingMessage, 10000);
      } catch (error) {
        console.error(`Failed to send ${command}`, error);
        openSnackbar(errorMessage, 4000);
        setUploading(false);
      }
    };
    reader.onerror = () => {
      openSnackbar(errorMessage, 4000);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  }, [caseid, command, errorMessage, openSnackbar, sendMessage, sendingMessage]);

  useEffect(() => {
    if (!isSocketOpen || !pendingFileRef.current) return;

    const pendingFile = pendingFileRef.current;
    pendingFileRef.current = null;
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    startUpload(pendingFile);
  }, [isSocketOpen, startUpload]);

  const handleFileChange = useCallback((event) => {
    const file = event.target.files && event.target.files[0];
    if (event.target) {
      event.target.value = '';
    }
    if (!file) return;

    if (accept) {
      const allowedExts = accept.split(',').map(ext => ext.trim().toLowerCase());
      const hasAllowedExt = allowedExts.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!hasAllowedExt) {
        openSnackbar(errorMessage, 4000);
        return;
      }
    }

    if (!isSocketOpen) {
      pendingFileRef.current = file;
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
      }
      openSnackbar('Waiting for server connection...', 4000);
      pendingTimerRef.current = setTimeout(() => {
        pendingFileRef.current = null;
        pendingTimerRef.current = null;
        openSnackbar(errorMessage, 4000);
      }, CONNECTION_WAIT_MS);
      return;
    }

    startUpload(file);
  }, [accept, errorMessage, isSocketOpen, openSnackbar, startUpload, CONNECTION_WAIT_MS]);

  return {
    uploading,
    inputRef,
    handleFileChange,
    triggerFileDialog
  };
}

export default useStorageUpload;
